/**
 * Compact ID prefixes for bookings tables:
 * I = iCal, E = AirBnB Export, M = Merged (single ID on Combined merged rows).
 * Plain Manual (non-Export) stays unprefixed.
 */

export type BookingIdDisplayInput = {
  id: number;
  source?: string | null;
  is_merged_pair?: boolean;
  linked_ical_booking_id?: number | null;
  linked_manual_booking_id?: number | null;
};

export function bookingIdPrefix(
  source?: string | null,
  isMergedPair?: boolean
): 'I' | 'E' | 'M' | '' {
  if (isMergedPair || source === 'Merged') return 'M';
  const s = String(source || '').trim();
  if (s === 'iCal') return 'I';
  if (s === 'AirBnB Export' || s === 'VRBO Export') return 'E';
  return '';
}

/** Display label e.g. "M 6179", "I 1692", "E 6179", or "42" for Manual. */
export function formatBookingIdDisplay(booking: BookingIdDisplayInput): string {
  const prefix = bookingIdPrefix(booking.source, booking.is_merged_pair);
  return prefix ? `${prefix} ${booking.id}` : String(booking.id);
}

/** Tooltip for merged rows listing both underlying IDs. */
export function mergedBookingIdTitle(booking: BookingIdDisplayInput): string {
  const icalId = booking.linked_ical_booking_id;
  const manualId = booking.linked_manual_booking_id;
  if (icalId == null || manualId == null) {
    return formatBookingIdDisplay(booking);
  }
  return `I ${icalId} · E/Manual ${manualId}`;
}

/** Match ID column filter against display label, numeric id, or linked pair ids. */
export function bookingIdMatchesFilter(booking: BookingIdDisplayInput, rawFilter: string): boolean {
  const q = rawFilter.trim();
  if (!q) return true;
  const qLower = q.toLowerCase();
  const display = formatBookingIdDisplay(booking).toLowerCase();
  if (display.includes(qLower)) return true;
  const digits = q.replace(/^[iem]\s*/i, '').trim();
  if (digits && String(booking.id).includes(digits)) return true;
  if (booking.linked_ical_booking_id != null && String(booking.linked_ical_booking_id).includes(digits || q)) {
    return true;
  }
  if (booking.linked_manual_booking_id != null && String(booking.linked_manual_booking_id).includes(digits || q)) {
    return true;
  }
  return false;
}
